'use client';

import { Lock } from '@phosphor-icons/react';
import { LegalPageLayout } from '@/components/legal/legal-page-layout';

const BulletList = ({ items, variant = 'default' }: { items: string[]; variant?: 'default' | 'danger' }) => (
  <ul className="space-y-1.5 pl-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2">
        <span className={`mt-1.5 w-1 h-1 rounded-pill flex-shrink-0 ${variant === 'danger' ? 'bg-[var(--error)]' : 'bg-[var(--primary)]'}`} />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Highlight = ({ children }: { children: React.ReactNode }) => (
  <div className="p-4 rounded-card bg-[var(--primary-subtle)] text-[var(--text-primary)] text-sm">
    {children}
  </div>
);

export function SecurePaymentsContent() {
  const sections = [
    {
      title: 'Оплата банковской картой',
      content: (
        <>
          <p>Банковская карта не может быть передана для оплаты заказа третьему лицу.</p>
          <p className="font-medium text-[var(--text-primary)]">При совершении операции с картой производятся следующие действия:</p>
          <BulletList items={[
            'Авторизация операции',
            'Проверка правильности PIN-кода',
            'Сверка номера карты с данными чека и подписи',
          ]} />
          <p className="font-medium text-[var(--text-primary)] mt-4">Карта не может быть использована, если:</p>
          <BulletList variant="danger" items={[
            'Истёк срок действия',
            'Карта заблокирована (неверный PIN-код или по требованию)',
            'Недостаточно средств на счёте',
          ]} />
        </>
      ),
    },
    {
      title: 'Онлайн-оплата',
      content: (
        <>
          <p>Оплата через интернет производится с соблюдением всех требований безопасности. Мы используем современные методы шифрования и защищённые каналы передачи данных.</p>
          <Highlight>
            <p>Ввод данных карты происходит на защищённой странице банка с использованием протокола SSL 3.0. Информация передаётся в зашифрованном виде.</p>
          </Highlight>
          <p className="font-medium text-[var(--text-primary)]">Возможные причины отказа в платеже:</p>
          <BulletList variant="danger" items={[
            'Карта не поддерживает интернет-платежи',
            'Недостаточно средств',
            'Неверно введены данные',
            'Превышен дневной лимит операций',
          ]} />
        </>
      ),
    },
    {
      title: 'Правила ввода данных карты',
      content: (
        <ol className="space-y-2 pl-1">
          {[
            'Имя владельца — латинскими буквами, как на карте',
            '16-значный номер карты, без пробелов',
            'Срок действия — месяц и год',
            'CVV2/CVC2 — 3 цифры с обратной стороны',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <span className="w-5 h-5 rounded-card bg-[var(--primary-subtle)] text-[var(--primary)] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
              <span>{item}</span>
            </li>
          ))}
        </ol>
      ),
    },
  ];

  return (
    <LegalPageLayout
      title="Безопасные платежи"
      icon={<Lock className="w-3.5 h-3.5 text-[var(--primary)]" weight="fill" />}
      sections={sections}
      breadcrumbs={[
        { name: 'Главная', href: '/' },
        { name: 'Безопасные платежи', href: '/secure-payments' },
      ]}
      contactBlock={
        <div className="text-sm text-[var(--text-secondary)]">
          <p>Вопросы по оплате:</p>
          <a href="tel:+78633335050" className="text-[var(--primary)] font-medium hover:underline">+7 (863) 333-50-50</a>
        </div>
      }
    />
  );
}
